import { useWindowSize } from '@leedpulse-monorepo/hooks';
import { ECreditType, creditTypeAsString, EProductType, productTypeAsString } from '@leedpulse-monorepo/enums';
import {
  Badge,
  Button,
  Col,
  Flex,
  Row,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  MessageOutlined,
  ContactsOutlined,
  ExportOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { SUBSCRIPTIONS_CHATS_PAGE_PATH } from '../../pages/subscriptions/SubscriptionChatsPage';
import { CONTACTS_PAGE_PATH } from '../../pages/contacts/ContactsPage';
import moment from 'moment';
import { advancedOnRow, formatCredit } from '../../../core/utils/commonUtils';
import { ISubscriptionView } from '../../../domain/interfaces/ISubscriptionView';

interface SubscriptionsListProps {
  subscriptions: ISubscriptionView[];
  loading: boolean;
  page: number;
  nbHits: number;
  hitsPerPage: number;
  onPageChange: (page: number) => void;
  onScrollToTop?: () => void;
  onPageSizeChange?: (newPageSize: number) => void;
}

const SubscriptionsList: React.FC<SubscriptionsListProps> = ({
  subscriptions,
  loading,
  page,
  nbHits,
  hitsPerPage,
  onPageChange,
  onScrollToTop,
  onPageSizeChange,
}) => {
  const windowSize = useWindowSize();
  const navigate = useNavigate();

  const handlePaginationChange = (antPage: number) => {
    onPageChange(antPage - 1);
    onScrollToTop?.();
  };

  const handlePageSizeChange = (_current: number, newPageSize: number) => {
    onPageSizeChange?.(newPageSize);
  };

  const renderSettingsButton = (record: ISubscriptionView) => (
    <Tooltip placement="left" title="Ouvrir dans un nouvel onglet">
      <Button
        type={'default'}
        style={{ color: '#6737ff' }}
        icon={<ExportOutlined style={{ color: '#6737ff' }} />}
        onClick={(e) => {
          e.stopPropagation();
          window.open(
            `/subscriptions/${record.objectID}`,
            '_blank',
            'noopener,noreferrer'
          );
        }}
      />
    </Tooltip>
  );

  const renderConversationsButton = (record: ISubscriptionView) => (
    <Button
      icon={<MessageOutlined />}
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/subscriptions/${record.objectID}${SUBSCRIPTIONS_CHATS_PAGE_PATH}`);
      }}
    >
      {windowSize.width > 576 ? 'Chats' : ''}
    </Button>
  );

  const renderContactsButton = (record: ISubscriptionView) => (
    <Button
      icon={<ContactsOutlined />}
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/subscriptions/${record.objectID}${CONTACTS_PAGE_PATH}`);
      }}
    >
      {windowSize.width > 576 ? 'Contacts' : ''}
    </Button>
  );

  const renderBadge = (count: number | null) => (
    <Badge
      count={count ?? 0}
      style={{
        backgroundColor:
          (count ?? 0) > 0 ? 'var(--danger-color)' : 'var(--border-color)',
        fontWeight: 'bold',
      }}
      showZero
    />
  );

  const renderColumnName = (record: ISubscriptionView) => {
    const planEnd = record.plan_end ? moment(record.plan_end) : null;
    const isExpired = planEnd ? planEnd.isBefore(new Date().getTime()) : false;

    return (
      <>
        <h3>
          {record.plan_product && (
            <Tag
              style={{
                marginRight: '0.1rem',
                backgroundColor: record.plan_product === EProductType.LEEDPULSE ? '#6737ff' : '#34c759',
                color: '#ffffff',
                border: 'none',
              }}
            >
              {productTypeAsString(record.plan_product as EProductType)}
            </Tag>
          )}
          <b>{record.name}</b>
        </h3>
        {planEnd && !isExpired && (
          <Tag color={'green'}>
            • Accès garanti jusqu'au {planEnd.format('DD/MM/YYYY')}
          </Tag>
        )}
        {planEnd && isExpired && (
          <Tag color={'red'}>
            • Compte expiré le {planEnd.format('DD/MM/YYYY')}
          </Tag>
        )}
      </>
    );
  };

  const columns = [
    {
      title: 'Nom',
      render: (record: ISubscriptionView) => (
        <Col>
          <Row>{renderColumnName(record)}</Row>
        </Col>
      ),
    },
    {
      title: 'Crédits',
      render: (record: ISubscriptionView) => (
        <Flex vertical>
          {record.plan_product === EProductType.LEEDFLOW ? (
            <Flex gap={8} align="center">
              <span title={creditTypeAsString(ECreditType.leedbucks, false)}>{creditTypeAsString(ECreditType.leedbucks)}</span>
              <span>{formatCredit(record.credits_leedbucks || 0)}</span>
            </Flex>
          ) : (
            <>
              <Flex gap={8} align="center">
                <span title={creditTypeAsString(ECreditType.sms, false)}>{creditTypeAsString(ECreditType.sms)}</span>
                <span>{formatCredit(record.credits_sms || 0)}</span>
              </Flex>
              <Flex gap={8} align="center">
                <span title={creditTypeAsString(ECreditType.minutes, false)}>{creditTypeAsString(ECreditType.minutes)}</span>
                <span>{formatCredit(record.credits_minutes || 0)}</span>
              </Flex>
              <Flex gap={8} align="center">
                <span title={creditTypeAsString(ECreditType.contacts, false)}>{creditTypeAsString(ECreditType.contacts)}</span>
                <span>{formatCredit(record.credits_contacts || 0)}</span>
              </Flex>
            </>
          )}
        </Flex>
      ),
    },
    {
      title: windowSize.width > 576 ? 'Reçus' : <InboxOutlined />,
      render: (record: ISubscriptionView) => renderBadge(record.messages_counters_user_unread),
    },
    {
      title: '',
      key: 'access',
      render: (record: ISubscriptionView) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
          {renderConversationsButton(record)}
          {renderContactsButton(record)}
          {renderSettingsButton(record)}
        </div>
      ),
    },
  ];

  return (
    <Table
      pagination={{
        current: page + 1,
        total: nbHits,
        pageSize: hitsPerPage,
        onChange: handlePaginationChange,
        onShowSizeChange: (_current, newSize) => handlePageSizeChange(_current, newSize),
        showSizeChanger: true,
        simple: true,
        pageSizeOptions: [20, 50, 100, 200],
        position: ['bottomRight', 'topRight'],
        disabled: loading,
      }}
      loading={loading}
      columns={columns}
      rowKey={(record) => record.objectID}
      onRow={(record) =>
        advancedOnRow({
          url: `/subscriptions/${record.objectID}`,
          navigate,
          className: 'table-row',
        })
      }
      dataSource={subscriptions}
    />
  );
};

export default SubscriptionsList;
